import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import {
	createMonitoringConnection,
	deleteMonitoringConnection,
	listMonitoringConnectors,
	listMonitoringConnections,
	reconnectMonitoringConnection,
	syncMonitoringTraffic,
	verifyMonitoringConnection,
} from "../../../../services/monitoringApi";
import {
	CAPABILITY_LABELS,
	CONNECTOR_STATUS_LABELS,
	SOURCE_LABELS,
} from "./trafficShape";

const STATUS_CLASS = {
	active: "monitoring__ai-traffic-connector-status--active",
	needs_auth: "monitoring__ai-traffic-connector-status--warn",
	error: "monitoring__ai-traffic-connector-status--error",
	revoked: "monitoring__ai-traffic-connector-status--muted",
};

export default function TrafficConnectorsPanel({
	websiteId,
	onChanged,
}) {
	const [catalogue, setCatalogue] = useState([]);
	const [connections, setConnections] = useState([]);
	const [loading, setLoading] = useState(true);
	const [openForm, setOpenForm] = useState(null);
	const [fields, setFields] = useState({});
	const [msg, setMsg] = useState(null);
	const [awaitingOAuth, setAwaitingOAuth] = useState(false);
	const [connectingKey, setConnectingKey] = useState(null);
	const [verifyingId, setVerifyingId] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		const [catRes, connRes] = await Promise.all([
			listMonitoringConnectors(),
			listMonitoringConnections(websiteId),
		]);
		setCatalogue(catRes.ok ? catRes.connectors : []);
		setConnections(connRes.ok ? connRes.connections : []);
		setLoading(false);
	}, [websiteId]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (!awaitingOAuth) return undefined;
		const timer = setInterval(load, 4000);
		const hasActive = connections.some(
			(c) => c.status === "active" && c.connector_key === "ga4"
		);
		if (hasActive) {
			setAwaitingOAuth(false);
			setMsg({ ok: true, text: "Google Analytics connected." });
			onChanged?.();
		}
		return () => clearInterval(timer);
	}, [awaitingOAuth, connections, load, onChanged]);

	const byConnector = useMemo(() => {
		const map = new Map();
		connections.forEach((c) => {
			if (c.status !== "revoked") map.set(c.connector_key, c);
		});
		return map;
	}, [connections]);

	async function afterChange(daysBack = 90) {
		await load();
		onChanged?.();
		try {
			await syncMonitoringTraffic(websiteId, daysBack);
		} catch {
			/* hourly tick will pick up */
		}
	}

	async function handleCreate(spec) {
		setConnectingKey(spec.key);
		const config = {};
		Object.keys(spec.config_schema || {}).forEach((k) => {
			if (fields[k]?.trim()) config[k] = fields[k].trim();
		});
		const credentials =
			spec.auth_kind === "api_token"
				? { api_token: (fields.api_token || "").trim() }
				: undefined;
		const res = await createMonitoringConnection(websiteId, {
			connectorKey: spec.key,
			config,
			credentials,
		});
		setConnectingKey(null);
		if (!res.ok) {
			setMsg({ ok: false, text: res.message || "Could not create connection." });
			return;
		}
		const conn = res.connection;
		setOpenForm(null);
		setFields({});
		if (conn?.authorize_url) {
			setAwaitingOAuth(true);
			setMsg({ ok: true, text: "Approve access in the Google tab that just opened…" });
			window.open(conn.authorize_url, "_blank", "noopener");
			load();
			return;
		}
		if (conn?.status === "active") {
			setMsg({ ok: true, text: `${conn.connector_name || spec.name} connected and verified.` });
			afterChange(90);
		} else {
			setMsg({ ok: false, text: conn?.last_error || "Could not verify the connection." });
			load();
		}
	}

	async function handleVerify(id) {
		setVerifyingId(id);
		const res = await verifyMonitoringConnection(id);
		setVerifyingId(null);
		if (!res.ok) {
			toast.error(res.message || "Verification failed");
			return;
		}
		setMsg({
			ok: !!res.connection?.verified,
			text: res.connection?.verified
				? "Verified — credentials and permissions are good."
				: res.connection?.error || "Verification failed.",
		});
		load();
	}

	async function handleReconnect(id) {
		const res = await reconnectMonitoringConnection(id);
		if (!res.ok) {
			toast.error(res.message || "Reconnect failed");
			return;
		}
		if (res.connection?.authorize_url) {
			setAwaitingOAuth(true);
			window.open(res.connection.authorize_url, "_blank", "noopener");
			setMsg({ ok: true, text: "Re-approve access in the Google tab…" });
		}
		load();
	}

	async function handleDisconnect(id) {
		const res = await deleteMonitoringConnection(id);
		if (!res.ok) {
			toast.error(res.message || "Disconnect failed");
			return;
		}
		setMsg({ ok: true, text: "Disconnected. The stored credential was deleted." });
		load();
		onChanged?.();
	}

	if (loading && !catalogue.length) {
		return <p className="monitoring__ai-traffic-empty mb-0">Loading connectors…</p>;
	}

	return (
		<div className="monitoring__ai-traffic-kpi-card monitoring__ai-traffic-connectors-panel">
			<h3 className="monitoring__ai-traffic-section-title">Data sources</h3>
			<p className="monitoring__ai-traffic-section-note mb-3">
				GA4 and Cloudflare measure different halves of AI traffic and are meant to
				run together: Cloudflare sees AI crawlers at the edge (GA4 cannot — it is
				JavaScript-based and filters bots), while GA4 sees the humans who arrive
				from AI answers, plus conversions and revenue.
			</p>

			{msg && (
				<div
					className={`monitoring__ai-traffic-banner mb-3 ${
						msg.ok
							? "monitoring__ai-traffic-banner--success"
							: "monitoring__ai-traffic-banner--error"
					}`}
				>
					{msg.text}
				</div>
			)}

			<div className="monitoring__ai-traffic-connector-grid">
				{catalogue.map((spec) => {
					const conn = byConnector.get(spec.key);
					const isOpen = openForm === spec.key;
					const caps = conn?.capabilities?.length
						? conn.capabilities
						: spec.capabilities || [];
					return (
						<div key={spec.key} className="monitoring__ai-traffic-connector-card">
							<div className="monitoring__ai-traffic-connector-body">
								<div className="monitoring__ai-traffic-connector-head">
									<span className="monitoring__ai-traffic-connector-name">
										{spec.name}
									</span>
									<div className="monitoring__ai-traffic-connector-head-badges">
										<span
											className={`monitoring__ai-traffic-connector-status ${
												conn
													? STATUS_CLASS[conn.status]
													: "monitoring__ai-traffic-connector-status--disconnected"
											}`}
										>
											{conn
												? CONNECTOR_STATUS_LABELS[conn.status] || conn.status
												: "Not connected"}
										</span>
										<span className="monitoring__ai-traffic-connector-meta">
											{spec.auth_kind === "oauth2" ? "OAuth" : "API token"} ·{" "}
											{conn?.transport ?? spec.default_transport}
										</span>
									</div>
								</div>
								<div className="monitoring__ai-traffic-connector-caps">
									{caps.map((c) => (
										<span key={c} className="monitoring__ai-traffic-cap-pill">
											{CAPABILITY_LABELS[c] ?? c}
										</span>
									))}
								</div>
								{conn?.last_error && (
									<p className="monitoring__ai-traffic-connector-error mb-0">
										{conn.last_error}
									</p>
								)}

								{(!conn || conn.status === "revoked") && isOpen && (
									<div className="monitoring__ai-traffic-connector-form">
										{Object.entries(spec.config_schema || {}).map(
											([field, rules]) => (
												<Input
													key={field}
													bsSize="sm"
													value={fields[field] ?? ""}
													onChange={(e) =>
														setFields({ ...fields, [field]: e.target.value })
													}
													placeholder={rules.label || field}
												/>
											)
										)}
										{spec.auth_kind === "api_token" && (
											<Input
												bsSize="sm"
												type="password"
												value={fields.api_token ?? ""}
												onChange={(e) =>
													setFields({ ...fields, api_token: e.target.value })
												}
												placeholder="API token"
											/>
										)}
										<div className="monitoring__ai-traffic-connector-form-actions">
											<Button
												size="sm"
												color="primary"
												className="monitoring__action-btn monitoring__action-btn--primary"
												disabled={connectingKey === spec.key}
												onClick={() => handleCreate(spec)}
											>
												{connectingKey === spec.key
													? "Connecting…"
													: spec.auth_kind === "oauth2"
														? "Continue with Google"
														: "Connect"}
											</Button>
											<Button
												size="sm"
												className="monitoring__action-btn"
												onClick={() => {
													setOpenForm(null);
													setFields({});
												}}
											>
												Cancel
											</Button>
										</div>
									</div>
								)}
							</div>

							<div className="monitoring__ai-traffic-connector-footer">
								{(!conn || conn.status === "revoked") && !isOpen && (
									<Button
										size="sm"
										color="primary"
										className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-connector-connect-btn"
										onClick={() => {
											setOpenForm(spec.key);
											setMsg(null);
										}}
									>
										Connect {spec.name}
									</Button>
								)}
								{conn && conn.status !== "revoked" && (
									<>
										<div className="monitoring__ai-traffic-connector-actions">
											<Button
												size="sm"
												color="primary"
												className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-connector-action-btn"
												disabled={verifyingId === conn.id}
												onClick={() => handleVerify(conn.id)}
											>
												{verifyingId === conn.id ? "Verifying…" : "Verify"}
											</Button>
											{spec.auth_kind === "oauth2" ? (
												<Button
													size="sm"
													color="primary"
													className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-connector-action-btn"
													onClick={() => handleReconnect(conn.id)}
												>
													Re-authorise
												</Button>
											) : (
												<span
													className="monitoring__ai-traffic-connector-action-placeholder"
													aria-hidden
												/>
											)}
											<button
												type="button"
												className="monitoring__ai-traffic-disconnect-btn monitoring__ai-traffic-connector-action-btn"
												onClick={() => handleDisconnect(conn.id)}
											>
												Disconnect
											</button>
										</div>
										{conn.last_verified_at && (
											<p className="monitoring__ai-traffic-connector-verified mb-0">
												verified{" "}
												{new Date(conn.last_verified_at).toLocaleString()}
											</p>
										)}
									</>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export { SOURCE_LABELS };
